// Öğretmen panelinin ortak iskeleti: üst çubuk, sayfa bağlantıları ve sağ üstteki hesap menüsü.
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { LuChevronDown, LuLogOut, LuShieldCheck, LuUser } from 'react-icons/lu';
import { planOf, type MeResponse } from '../../shared/plans';
import { logout, useMe } from '../lib/api';
import { CenterCard, NovaMark } from './ui';

export type Me = Required<MeResponse> & { user: NonNullable<MeResponse['user']> };
export type TeacherPage = 'panel' | 'dersler' | 'ogrenciler' | 'profil';

const NAV: { id: TeacherPage; href: string; label: string }[] = [
  { id: 'panel', href: '/', label: 'Panel' },
  { id: 'dersler', href: '/derslerim', label: 'Derslerim' },
  { id: 'ogrenciler', href: '/ogrencilerim', label: 'Öğrencilerim' },
];

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toLocaleUpperCase('tr'))
    .join('');

function UserMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={root}>
      <button type="button" className="user-trigger" aria-label="Hesap menüsü" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="avatar" aria-hidden="true">
          {initials(me.user.name)}
        </span>
        <span className="user-trigger-name">{me.user.name}</span>
        {me.adminOpenRequests ? <span className="notify-dot" aria-label={`${me.adminOpenRequests} bekleyen paket talebi`} /> : null}
        <LuChevronDown />
      </button>
      {open && (
        <div className="user-dropdown">
          <div className="user-dropdown-head">
            <strong>{me.user.name}</strong>
            <span className="muted small">{me.user.email}</span>
            <span className="plan-status is-ok user-plan">{planOf(me.account.plan).label} paketi</span>
          </div>
          <a href="/profil">
            <LuUser /> Profil ve paket
          </a>
          {me.user.isAdmin && (
            <a href="/yonetim">
              <LuShieldCheck /> Yönetim
              {me.adminOpenRequests ? <span className="notify-count">{me.adminOpenRequests}</span> : null}
            </a>
          )}
          <button type="button" onClick={() => void logout().finally(() => location.assign('/'))}>
            <LuLogOut /> Çıkış yap
          </button>
        </div>
      )}
    </div>
  );
}

export function AppShell({ me, page, title, subtitle, actions, children }: { me: Me; page: TeacherPage; title: string; subtitle?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="app">
      <header className="app-bar">
        <div className="app-bar-inner">
          <a className="brand" href="/" aria-label="Nova Class panel">
            <NovaMark size={32} />
            <span className="brand-name">Nova Class</span>
          </a>
          <nav className="app-nav" aria-label="Panel">
            {NAV.map((n) => (
              <a key={n.id} href={n.href} className={page === n.id ? 'is-active' : ''} aria-current={page === n.id ? 'page' : undefined}>
                {n.label}
              </a>
            ))}
          </nav>
          <UserMenu me={me} />
        </div>
      </header>
      <main className="app-main">
        <div className="app-title">
          <div>
            <h1>{title}</h1>
            {subtitle && <p className="muted">{subtitle}</p>}
          </div>
          {actions && <div className="app-actions">{actions}</div>}
        </div>
        {children}
      </main>
    </div>
  );
}

/** Girişli ve doğrulanmış öğretmen gerektiren sayfalar; kalanlar uygun yere yönlenir. */
export function RequireTeacher({ children }: { children: (me: Me, refresh: () => void) => ReactNode }) {
  const { me, error, refresh } = useMe();
  const ready = me?.user && me.account && !me.user.mustChangePassword && me.user.emailVerified;
  useEffect(() => {
    if (!me || ready) return;
    location.replace(me.user ? '/' : '/giris');
  }, [me, ready]);
  if (error) return <CenterCard title="Sunucuya ulaşılamadı" text={error} />;
  if (!ready || !me) return <div className="center-screen" aria-busy="true" />;
  return <>{children(me as Me, () => void refresh())}</>;
}
